import { AbilitySwitch, IfAbility } from '@efdigital/ll-addon-auth';
import { DataTypeXceed } from '@efdigital/ll-addon-data/data-types/xceed';
import type { Program } from '@efdigital/ll-addon-data/types/xceed/program';
import { Heading } from '@efdigital/ll-addon-ui';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { EditProgram } from '@efdigital/ll-admin-v2/components/programs/edit-program.gts';
import { ResultsTable } from '@efdigital/ll-admin-v2/components/results-table.gts';
import {
  SearchPrograms,
  SearchProgramsState,
} from '@efdigital/ll-admin-v2/components/search/search-programs';
import { formatTimestamp } from '@efdigital/ll-admin-v2/helpers/format-timestamp';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import type AbilityService from '@efdigital/ll-admin-v2/services/ability';
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Await, Request } from '@warp-drive/ember';
import BsModal from 'ember-bootstrap/components/bs-modal';

function formatPageRange(jsonApiMetaPage: unknown, count: number) {
  if (typeof jsonApiMetaPage !== 'object' || jsonApiMetaPage === null) {
    return '';
  }
  const { offset, total } = jsonApiMetaPage as {
    offset: number;
    total: number;
  };
  if (typeof offset !== 'number' || typeof total !== 'number') {
    return '';
  }
  return `${offset + 1} - ${offset + count} of ${total}`;
}

export default class ProgramsRoute extends Component<{
  Args: {
    model: {
      id: string;
    };
  };
}> {
  @service declare readonly store: Store;
  @service declare readonly router: RouterService;
  @service declare readonly ability: AbilityService;
  @tracked state = new SearchProgramsState();
  @tracked modalOpen = false;
  @tracked newProgram?: Program;

  setState = (state: Partial<SearchProgramsState>) => {
    Object.assign(this.state, state);
  };

  showCreateModal = () => {
    this.newProgram = this.store.createRecord<Program>(DataTypeXceed.Program, {
      slug: generateRandomSlug(),
    });
    this.modalOpen = true;
  };

  hideCreateModal = () => {
    this.modalOpen = false;
    this.newProgram = undefined;
  };

  programCreated = () => {
    this.router.transitionTo('programs.program.index', this.newProgram!.id!);
  };

  nextPage = (event: MouseEvent) => {
    event.preventDefault();
    this.state.offset += 5;
  };

  previousPage = (event: MouseEvent) => {
    event.preventDefault();
    this.state.offset -= 5;
  };

  <template>
    <div class="container py-5">
      <Heading @text="Programs" />

      <AbilitySwitch @ability="programs">
        <:viewer>
          <SearchPrograms
            @legend="Program filters"
            @state={{this.state}}
            @setState={{this.setState}}
          >
            <:query as |query|>
              <Request @query={{query}}>
                <:content as |response|>
                  <p>Showing
                    {{formatPageRange
                      response.meta.page
                      response.data.length
                    }}</p>
                  {{#if response.links.next}}
                    <button
                      type="button"
                      class="btn btn-primary"
                      {{on "click" this.nextPage}}
                    >Next Page</button>
                  {{/if}}
                  {{#if response.links.prev}}
                    <button
                      type="button"
                      class="btn btn-primary"
                      {{on "click" this.previousPage}}
                    >Previous Page</button>
                  {{/if}}

                  <ResultsTable @data={{response.data}}>
                    <:header>
                      <th scope="col">Title</th>
                      <th scope="col">Client</th>
                      <th scope="col">Modified</th>
                    </:header>
                    <:row as |program|>
                      <tr>
                        <th scope="row"><LinkTo
                            @route="programs.program.index"
                            @models={{array program.id}}
                          >{{program.title}}</LinkTo></th>
                        <td>
                          <Await @promise={{program.client}}>
                            <:success as |client|>
                              {{client.title}}
                            </:success>
                          </Await>
                        </td>
                        <td class="font-monospace">{{formatTimestamp
                            program.modified
                          }}</td>
                      </tr>
                    </:row>
                  </ResultsTable>
                </:content>
                <:error as |error retry|>
                  <div class="alert alert-danger" role="alert">
                    <h4 class="alert-heading">Error Loading Programs</h4>
                    <p>
                      {{if
                        error.message
                        error.message
                        "There was a problem loading programs. Please try again."
                      }}
                    </p>
                    {{#if retry}}
                      <button
                        type="button"
                        class="btn btn-secondary"
                        {{on "click" retry.retry}}
                      >Retry</button>
                    {{/if}}
                  </div>
                </:error>
              </Request>
            </:query>
          </SearchPrograms>
        </:viewer>
        <:none>
          <div class="alert alert-warning" role="alert">
            <h4 class="alert-heading">Access Denied</h4>
            <p>You do not have permission to view programs.</p>
            <p class="mb-0">Please contact your administrator if you believe you
              should have access.</p>
          </div>
        </:none>
      </AbilitySwitch>

      <IfAbility @ability="programs" @atLeast="editor">
        <:if>
          <hr class="my-5" />

          <div class="d-flex justify-content-center mb-5">
            <button
              type="button"
              class="btn btn-primary btn-md"
              {{on "click" this.showCreateModal}}
            >New Program</button>
          </div>

          <BsModal
            @open={{this.modalOpen}}
            @onHidden={{this.hideCreateModal}}
            @backdropClose={{false}}
            @size="xl"
            as |modal|
          >
            <modal.header>
              <h3 class="modal-title h4">Create Program</h3>
            </modal.header>
            <modal.body>
              {{#if this.newProgram}}
                <EditContext
                  @onEditingStopped={{this.programCreated}}
                  as |context|
                >
                  <EditProgram @item={{this.newProgram}} @context={{context}} />
                </EditContext>
              {{/if}}
            </modal.body>
          </BsModal>
        </:if>
      </IfAbility>
    </div>
  </template>
}
